/**
 * Global Configuration Context v1.0
 * 
 * Provides centralized configuration management with real-time Firebase sync.
 * All components that need config data should consume this context.
 * 
 * Features:
 * - Real-time updates from Firebase
 * - Fallback to localStorage
 * - Type-safe configuration access
 * - Automatic sync across tabs/windows
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Production-safe logger - only logs in development
const isDev = process.env.NODE_ENV === 'development';
const devLog = (message: string, ...args: any[]) => {
    if (isDev) console.log(message, ...args);
};
import {
    getShopConfig,
    listenToShopConfig,
    getGlobalConfig,
    listenToGlobalConfig,
    saveShopConfig,
    saveGlobalConfig,
    getLandingConfig,
    listenToLandingConfig,
    saveLandingConfig
} from '../services/firebaseService';
import { LandingPageContentConfig, DEFAULT_LANDING_CONFIG } from '../config/centralConfig';
import {
    ShopConfig,
    DEFAULT_SHOP_CONFIG,
    loadCentralConfig,
    saveCentralConfig,
    CentralConfig,
    DEFAULT_SYSTEM_CONFIG
} from '../config/centralConfig';
import { GlobalConfig } from '../types';

interface ConfigContextValue {
    // Shop config (packages, pricing)
    shopConfig: ShopConfig;
    setShopConfig: (config: ShopConfig) => void;
    saveShopConfigToFirebase: (config: ShopConfig) => Promise<void>;

    // Global app config
    globalConfig: GlobalConfig | null;
    setGlobalConfig: (config: GlobalConfig) => void;

    // Landing page config
    landingConfig: LandingPageContentConfig;
    setLandingConfig: (config: LandingPageContentConfig) => void;
    saveLandingConfigToFirebase: (config: LandingPageContentConfig) => Promise<void>;

    // Central config (local)
    centralConfig: CentralConfig;

    // Loading states
    isLoading: boolean;
    isShopConfigLoaded: boolean;
    isGlobalConfigLoaded: boolean;
    isLandingConfigLoaded: boolean;

    // Connection status
    firebaseConnected: boolean;
    lastSyncTime: number | null;

    // Force refresh
    refreshConfig: () => Promise<void>;

    // Version for forcing re-renders
    // Helper accessors for system config
    uiConfig: CentralConfig['system']['ui'];
    featureConfig: CentralConfig['system']['features'];
    contentConfig: CentralConfig['system']['content'];

    configVersion: number;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
};

// Optional hook that doesn't throw if context is missing
export const useConfigOptional = () => {
    return useContext(ConfigContext);
};

interface ConfigProviderProps {
    children: React.ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
    const [shopConfig, setShopConfigState] = useState<ShopConfig>(DEFAULT_SHOP_CONFIG);
    const [globalConfig, setGlobalConfigState] = useState<GlobalConfig | null>(null);
    const [landingConfig, setLandingConfigState] = useState<LandingPageContentConfig>(DEFAULT_LANDING_CONFIG);
    const [centralConfig, setCentralConfig] = useState<CentralConfig>(loadCentralConfig());

    const [isLoading, setIsLoading] = useState(true);
    const [isShopConfigLoaded, setIsShopConfigLoaded] = useState(false);
    const [isGlobalConfigLoaded, setIsGlobalConfigLoaded] = useState(false);
    const [isLandingConfigLoaded, setIsLandingConfigLoaded] = useState(false);
    const [firebaseConnected, setFirebaseConnected] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
    const [configVersion, setConfigVersion] = useState(0);

    const hasInitialized = useRef(false);

    // Load initial config
    const loadInitialConfig = useCallback(async () => {
        devLog('[ConfigContext] Loading initial config...');
        setIsLoading(true);

        try {
            // Parallel fetch for faster startup
            const [shopResult, globalResult, landingResult] = await Promise.allSettled([
                getShopConfig(),
                getGlobalConfig(),
                getLandingConfig()
            ]);

            // Process Shop Config
            if (shopResult.status === 'fulfilled' && shopResult.value) {
                const firebaseShopConfig = shopResult.value;
                devLog('[ConfigContext] Shop config loaded from Firebase');
                setShopConfigState(firebaseShopConfig as ShopConfig);
                setFirebaseConnected(true);

                // Also save to localStorage as backup
                const central = loadCentralConfig();
                central.shop = firebaseShopConfig as ShopConfig;
                saveCentralConfig(central);
            } else {
                if (shopResult.status === 'rejected') console.error('Shop fetch failed:', shopResult.reason);
                // Fallback to localStorage
                const central = loadCentralConfig();
                if (central.shop) {
                    devLog('[ConfigContext] Shop config loaded from localStorage');
                    setShopConfigState(central.shop);
                }
            }
            setIsShopConfigLoaded(true);

            // Process Global Config
            if (globalResult.status === 'fulfilled' && globalResult.value) {
                const firebaseConfig = globalResult.value;
                devLog('[ConfigContext] Global config loaded from Firebase');

                // AUTO-MIGRATE: Update old models to gemini-2.5-flash-lite
                let needsUpdate = false;
                const config = firebaseConfig as GlobalConfig;

                if (config.freeModel === 'gemini-2.0-flash') {
                    config.freeModel = 'gemini-2.5-flash-lite';
                    needsUpdate = true;
                    console.log('[ConfigContext] Auto-migrated freeModel');
                }

                if (config.premiumModel === 'gemini-2.0-flash') {
                    config.premiumModel = 'gemini-2.5-flash-lite';
                    needsUpdate = true;
                    console.log('[ConfigContext] Auto-migrated premiumModel');
                }

                if (config.maxModel === 'gemini-2.0-flash') {
                    config.maxModel = 'gemini-2.5-flash-lite';
                    needsUpdate = true;
                    console.log('[ConfigContext] Auto-migrated maxModel');
                }

                // Save back to Firebase if migrated
                if (needsUpdate) {
                    try {
                        await saveGlobalConfig(config);
                        console.log('[ConfigContext] Auto-migration saved to Firebase');
                    } catch (err) {
                        console.error('[ConfigContext] Failed to save migrated config:', err);
                    }
                }

                setGlobalConfigState(config);
            }
            setIsGlobalConfigLoaded(true);

            // Process Landing Config
            if (landingResult.status === 'fulfilled' && landingResult.value) {
                const firebaseLandingConfig = landingResult.value;
                devLog('[ConfigContext] Landing config loaded from Firebase');
                setLandingConfigState({ ...DEFAULT_LANDING_CONFIG, ...firebaseLandingConfig } as LandingPageContentConfig);
            } else {
                if (landingResult.status === 'rejected') console.error('Landing fetch failed:', landingResult.reason);
                // Fallback to localStorage
                const saved = localStorage.getItem('luca_landing_content_config');
                if (saved) {
                    try {
                        setLandingConfigState({ ...DEFAULT_LANDING_CONFIG, ...JSON.parse(saved) });
                    } catch (e) {
                        console.error('[ConfigContext] Failed to parse landing config:', e);
                    }
                }
            }
            setIsLandingConfigLoaded(true);

            setLastSyncTime(Date.now());
            setConfigVersion(prev => prev + 1);
        } catch (error) {
            console.error('[ConfigContext] Failed to load config from Firebase:', error);

            // Fallback to localStorage
            const central = loadCentralConfig();
            if (central.shop) {
                setShopConfigState(central.shop);
            }
            setIsShopConfigLoaded(true);
            setIsGlobalConfigLoaded(true);
            setIsLandingConfigLoaded(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initialize on mount
    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        loadInitialConfig();
    }, [loadInitialConfig]);

    // Listen for real-time config updates - combined for performance
    useEffect(() => {
        devLog('[ConfigContext] Setting up config listeners...');

        // Shop config listener
        const unsubscribeShop = listenToShopConfig((config) => {
            if (config) {
                devLog('[ConfigContext] Shop config update');
                setShopConfigState(config as ShopConfig);
                setFirebaseConnected(true);
                setLastSyncTime(Date.now());
                setConfigVersion(prev => prev + 1);

                // Update localStorage backup
                const central = loadCentralConfig();
                central.shop = config as ShopConfig;
                saveCentralConfig(central);

                window.dispatchEvent(new CustomEvent('shopConfigUpdated', { detail: config }));
            }
        });

        // Global config listener
        const unsubscribeGlobal = listenToGlobalConfig((config) => {
            if (config) {
                devLog('[ConfigContext] Global config update');
                setGlobalConfigState(config);
                setFirebaseConnected(true);
                setLastSyncTime(Date.now());
                setConfigVersion(prev => prev + 1);

                window.dispatchEvent(new CustomEvent('globalConfigUpdated', { detail: config }));
            }
        });

        // Landing config listener
        const unsubscribeLanding = listenToLandingConfig((config) => {
            if (config) {
                devLog('[ConfigContext] Landing config update');
                setLandingConfigState({ ...DEFAULT_LANDING_CONFIG, ...config } as LandingPageContentConfig);
                setFirebaseConnected(true);
                setLastSyncTime(Date.now());
                setConfigVersion(prev => prev + 1);

                window.dispatchEvent(new CustomEvent('landingConfigUpdated', { detail: config }));
            }
        });

        return () => {
            devLog('[ConfigContext] Cleaning up config listeners');
            unsubscribeShop();
            unsubscribeGlobal();
            unsubscribeLanding();
        };
    }, []);

    // Cross-tab sync via localStorage events
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'luca_central_config' && e.newValue) {
                try {
                    const parsed = JSON.parse(e.newValue);
                    if (parsed.shop) {
                        devLog('[ConfigContext] Config updated from another tab');
                        setShopConfigState(parsed.shop);
                        setConfigVersion(prev => prev + 1);
                    }
                } catch {
                    // Silently ignore parse errors in production
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Set shop config (also updates localStorage)
    const setShopConfig = useCallback((config: ShopConfig) => {
        setShopConfigState(config);
        setConfigVersion(prev => prev + 1);

        // Update localStorage
        const central = loadCentralConfig();
        central.shop = config;
        saveCentralConfig(central);
    }, []);

    // Save shop config to Firebase
    const saveShopConfigToFirebase = useCallback(async (config: ShopConfig) => {
        try {
            await saveShopConfig(config);
            setShopConfigState(config);
            setFirebaseConnected(true);
            setLastSyncTime(Date.now());
            setConfigVersion(prev => prev + 1);

            // Also update localStorage
            const central = loadCentralConfig();
            central.shop = config;
            saveCentralConfig(central);

            devLog('[ConfigContext] Shop config saved to Firebase');
        } catch (error) {
            console.error('[ConfigContext] Failed to save shop config to Firebase:', error);
            throw error;
        }
    }, []);

    // Set global config
    const setGlobalConfig = useCallback((config: GlobalConfig) => {
        setGlobalConfigState(config);
        setConfigVersion(prev => prev + 1);
    }, []);

    // Set landing config (also updates localStorage)
    const setLandingConfig = useCallback((config: LandingPageContentConfig) => {
        setLandingConfigState(config);
        setConfigVersion(prev => prev + 1);

        // Update localStorage
        localStorage.setItem('luca_landing_content_config', JSON.stringify(config));
    }, []);

    // Save landing config to Firebase
    const saveLandingConfigToFirebase = useCallback(async (config: LandingPageContentConfig) => {
        try {
            await saveLandingConfig(config);
            setLandingConfigState(config);
            setFirebaseConnected(true);
            setLastSyncTime(Date.now());
            setConfigVersion(prev => prev + 1);

            devLog('[ConfigContext] Landing config saved to Firebase');
        } catch (error) {
            console.error('[ConfigContext] Failed to save landing config to Firebase:', error);
            throw error;
        }
    }, []);

    // Force refresh all configs
    const refreshConfig = useCallback(async () => {
        devLog('[ConfigContext] Force refreshing config...');
        await loadInitialConfig();
    }, [loadInitialConfig]);

    const value: ConfigContextValue = {
        shopConfig,
        setShopConfig,
        saveShopConfigToFirebase,
        globalConfig,
        setGlobalConfig,
        landingConfig,
        setLandingConfig,
        saveLandingConfigToFirebase,
        centralConfig,
        isLoading,
        isShopConfigLoaded,
        isGlobalConfigLoaded,
        isLandingConfigLoaded,
        firebaseConnected,
        lastSyncTime,
        refreshConfig,
        configVersion,

        // Helpers (fallback to defaults if not loaded yet)
        uiConfig: centralConfig.system?.ui || DEFAULT_SYSTEM_CONFIG.ui,
        featureConfig: centralConfig.system?.features || DEFAULT_SYSTEM_CONFIG.features,
        contentConfig: centralConfig.system?.content || DEFAULT_SYSTEM_CONFIG.content,
    };

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    );
};

export default ConfigContext;

